import { useMemo, type FC, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  trendMetricsForPlatform,
  type PaidMediaOverviewGranularity,
  type PaidMediaOverviewSummaryPlatform,
  type PaidMediaOverviewTrendPoint,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewCurrency,
  formatOverviewMultiplier,
  formatOverviewUnitCost,
} from "../../utils/paidMediaOverviewFormat";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import {
  OverviewChartEmptyState,
  OverviewChartLoadingState,
  OverviewMetricTip,
} from "./overviewPrimitives";
import {
  OVERVIEW_BENCHMARK_COLOR,
  OVERVIEW_GRID_COLOR,
  OVERVIEW_SPIKE_COLOR,
  formatOverviewBucketLabel,
  overviewAxisProps,
} from "./overviewChartConfig";
import { OVERVIEW_PLATFORM_DESCRIPTORS } from "../../data/paidMediaOverviewPlatforms";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";

const CPA_SPIKE_THRESHOLD = 1.1;

interface EfficiencyChartRow {
  date: string;
  [seriesKey: string]: string | number | null;
}

const roasKey = (slug: string) => `${slug}Roas`;
const cpaKey = (slug: string) => `${slug}Cpa`;
const spikeKey = (slug: string) => `${slug}Spike`;

const numericValue = (row: EfficiencyChartRow, key: string): number | null => {
  const value = row[key];
  return typeof value === "number" ? value : null;
};

const spikeValue = (current: number | null, previous: number | null): number | null => {
  if (current === null || previous === null || !Number.isFinite(current) || !Number.isFinite(previous)) {
    return null;
  }
  return current > previous * CPA_SPIKE_THRESHOLD ? current : null;
};

interface EfficiencyRowProps {
  series: PaidMediaOverviewTrendPoint[];
  platforms: PaidMediaOverviewSummaryPlatform[];
  granularity: PaidMediaOverviewGranularity;
  roasBenchmark: number | null;
  currency: string | null;
  loading: boolean;
}

const EfficiencyRow: FC<EfficiencyRowProps> = ({
  series,
  platforms,
  granularity,
  roasBenchmark,
  currency,
  loading,
}) => {
  const { t, i18n } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  const data: EfficiencyChartRow[] = useMemo(
    () =>
      (series ?? []).map(point => {
        const row: EfficiencyChartRow = {
          date: formatOverviewBucketLabel(
            point?.date ?? "",
            granularity,
            date => t("paidMediaOverview.trend.weekOf", { date }),
            i18n.language
          ),
        };

        OVERVIEW_PLATFORM_DESCRIPTORS.forEach(descriptor => {
          const metrics = trendMetricsForPlatform(point, descriptor.key);
          row[roasKey(descriptor.slug)] = metrics?.roas ?? null;
          row[cpaKey(descriptor.slug)] = metrics?.cost_per_purchase ?? null;
        });

        return row;
      }),
    [series, granularity, t, i18n.language]
  );

  const cpaData: EfficiencyChartRow[] = useMemo(
    () =>
      data.map((row, index) => {
        const previous = index > 0 ? data[index - 1] : row;
        const withSpikes: EfficiencyChartRow = { ...row };
        OVERVIEW_PLATFORM_DESCRIPTORS.forEach(descriptor => {
          withSpikes[spikeKey(descriptor.slug)] = spikeValue(
            numericValue(row, cpaKey(descriptor.slug)),
            numericValue(previous, cpaKey(descriptor.slug))
          );
        });
        return withSpikes;
      }),
    [data]
  );

  const spikeKeys = useMemo(
    () => OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => spikeKey(descriptor.slug)),
    []
  );

  const summaryFor = (platform: PaidMediaOverviewSummaryPlatform["platform"]) =>
    platforms?.find(entry => entry?.platform === platform);

  const hasBenchmark = roasBenchmark !== null && Number.isFinite(roasBenchmark);
  const benchmarkLabel = t("paidMediaOverview.efficiency.benchmarkLabel", {
    value: formatOverviewMultiplier(roasBenchmark, 1),
  });

  const renderChart = (content: ReactElement) => {
    if (loading) {
      return <OverviewChartLoadingState />;
    }
    if (!data.length) {
      return <OverviewChartEmptyState />;
    }
    return <ResponsiveContainer>{content}</ResponsiveContainer>;
  };

  return (
    <div className="paid-media-overview__two-col-grid">
      <article className="paid-media-panel">
        <div className="paid-media-overview__panel-header">
          <div>
            <h3>
              {t("paidMediaOverview.efficiency.roasTitle")}
              <OverviewMetricTip id="roas" label={t("paidMediaOverview.efficiency.roasTitle")} />
            </h3>
            <p className="paid-media-overview__panel-subtitle">
              {t("paidMediaOverview.efficiency.roasSubtitle", {
                value: formatOverviewMultiplier(roasBenchmark, 1),
              })}
            </p>
          </div>
          <div className="paid-media-overview__legend-inline">
            {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
              <span
                key={descriptor.key}
                className={`paid-media-overview__legend-inline-item paid-media-overview__legend-inline-item--${descriptor.slug}`}
              >
                {t("paidMediaOverview.efficiency.averageFor", {
                  platform: t(descriptor.labelKey),
                  value: formatOverviewMultiplier(summaryFor(descriptor.key)?.roas),
                })}
              </span>
            ))}
          </div>
        </div>
        <div className="paid-media-overview__chart paid-media-overview__chart--sm">
          {renderChart(
            <LineChart data={data} margin={chartRtl.margin({ top: 5, right: 14, bottom: 5, left: -10 })}>
              <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
              <XAxis dataKey="date" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
              <YAxis
                tickFormatter={value => formatOverviewMultiplier(value, 0)}
                orientation={chartRtl.yAxisOrientation}
                {...overviewAxisProps}
              />
              <Tooltip
                wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
                content={
                  <CustomChartTooltip
                    formatTooltip={(value: number) => formatOverviewMultiplier(value)}
                  />
                }
              />
              {hasBenchmark ? (
                <ReferenceLine
                  y={roasBenchmark as number}
                  stroke={OVERVIEW_BENCHMARK_COLOR}
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                >
                  <Label
                    value={benchmarkLabel}
                    position={chartRtl.labelPosition}
                    className="benchmark-reference-label"
                  />
                </ReferenceLine>
              ) : null}
              {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
                <Line
                  key={descriptor.key}
                  type="monotone"
                  dataKey={roasKey(descriptor.slug)}
                  name={t("paidMediaOverview.efficiency.seriesRoasFor", {
                    platform: t(descriptor.labelKey),
                  })}
                  stroke={descriptor.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          )}
        </div>
      </article>

      <article className="paid-media-panel">
        <div className="paid-media-overview__panel-header">
          <div>
            <h3>
              {t("paidMediaOverview.efficiency.cpaTitle")}
              <OverviewMetricTip
                id="costPerPurchase"
                label={t("paidMediaOverview.efficiency.cpaTitle")}
              />
            </h3>
            <p className="paid-media-overview__panel-subtitle">
              {t("paidMediaOverview.efficiency.cpaSubtitle")}
            </p>
          </div>
          <div className="paid-media-overview__legend-inline">
            {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
              <span
                key={descriptor.key}
                className={`paid-media-overview__legend-inline-item paid-media-overview__legend-inline-item--${descriptor.slug}`}
              >
                {t("paidMediaOverview.efficiency.averageFor", {
                  platform: t(descriptor.labelKey),
                  value: formatOverviewUnitCost(
                    summaryFor(descriptor.key)?.cost_per_purchase,
                    currency
                  ),
                })}
              </span>
            ))}
          </div>
        </div>
        <div className="paid-media-overview__chart paid-media-overview__chart--sm">
          {renderChart(
            <LineChart data={cpaData} margin={chartRtl.margin({ top: 5, right: 10, bottom: 5, left: -10 })}>
              <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
              <XAxis dataKey="date" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
              <YAxis
                tickFormatter={value => formatOverviewCurrency(value, currency)}
                orientation={chartRtl.yAxisOrientation}
                {...overviewAxisProps}
              />
              <Tooltip
                wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
                content={({ active, payload, label }) => (
                  <CustomChartTooltip
                    active={active}
                    label={label}
                    payload={payload?.filter(
                      item => !spikeKeys.includes(String(item?.dataKey ?? ""))
                    )}
                    formatTooltip={(value: number) =>
                      formatOverviewCurrency(value, currency, { compact: false })
                    }
                  />
                )}
              />
              {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
                <Line
                  key={descriptor.key}
                  type="monotone"
                  dataKey={cpaKey(descriptor.slug)}
                  name={t("paidMediaOverview.efficiency.seriesCpaFor", {
                    platform: t(descriptor.labelKey),
                  })}
                  stroke={descriptor.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
              {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
                <Line
                  key={`${descriptor.key}-spike`}
                  type="monotone"
                  dataKey={spikeKey(descriptor.slug)}
                  stroke={OVERVIEW_SPIKE_COLOR}
                  strokeWidth={0}
                  dot={{ r: 4, fill: OVERVIEW_SPIKE_COLOR }}
                  legendType="none"
                  tooltipType="none"
                />
              ))}
            </LineChart>
          )}
        </div>
      </article>
    </div>
  );
};

export default EfficiencyRow;
